import { useEffect, useRef } from "react";

interface UsePollingParams {
    /** When false, polling is disabled (and any running loop is torn down). */
    enabled: boolean;
    /** Steady-state gap between successive polls, in ms. */
    interval: number;
    /**
     * Delay before the first poll, in ms. Defaults to 0 (poll immediately).
     * Callers can pass `interval` here to skip the leading poll when data is still fresh.
     */
    initialDelay?: number;
    /**
     * The work to run on each tick. Receives an `isActive` getter so long-running
     * async callbacks can bail out after an unmount / dependency change instead of
     * dispatching into a stale render.
     */
    onPoll: (isActive: () => boolean) => Promise<unknown> | unknown;
}

/**
 * Generic self-scheduling poller. Each tick waits for `onPoll` to settle before
 * scheduling the next one (no overlap), and the whole loop is cancelled on unmount
 * or when `enabled`/`initialDelay` change.
 *
 * `onPoll` is read through a ref, so callers may pass a fresh closure every render
 * without restarting the loop — only `enabled` and `initialDelay` drive re-arming.
 */
export const usePolling = ({ enabled, interval, initialDelay = 0, onPoll }: UsePollingParams): void => {
    const onPollRef = useRef(onPoll);
    useEffect(() => {
        onPollRef.current = onPoll;
    });

    useEffect(() => {
        if (!enabled) return;

        let isPollingActive = true;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const isActive = () => isPollingActive;

        const scheduleNext = () => {
            if (!isActive()) return;
            timeoutId = setTimeout(async () => {
                if (!isActive()) return;
                await onPollRef.current?.(isActive);
                if (isActive()) scheduleNext();
            }, interval);
        };

        const start = async () => {
            await onPollRef.current?.(isActive);
            if (isActive()) scheduleNext();
        };

        timeoutId = setTimeout(start, initialDelay);

        return () => {
            isPollingActive = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [enabled, initialDelay, interval]);
};
